import type { UseQueryOptions } from '@tanstack/react-query';

import { ROVER_SEARCH_SOURCE_HEADER } from '@rover/constants/src/platform/headers.constants';
import { ROVER_SEARCH_SOURCE_TYPE_LANDING_PAGE } from '@rover/constants/src/search/search.constants';
import { getApiSearchOptimizedRetrieveQueryKey } from '@rover/rsdk/src/apiClient/latest/api-search-optimized-retrieve';
import type { ApiSearchOptimizedRetrieveParams } from '@rover/rsdk/src/apiClient/latest/model/apiSearchOptimizedRetrieveParams';
import { OptimizedSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResponse';
import { HTTP } from '@rover/rsdk/src/modules/Network/orvalHTTPInterface';

type MakeSearchQueryOptions = {
  /** Additional params to merge (e.g. centerlat, centerlng) */
  locationParams?: Partial<ApiSearchOptimizedRetrieveParams>;
  /** Whether the query should be enabled */
  enabled?: boolean;
  /** Search source type header value, defaults to LANDING_PAGE */
  searchSourceType?: number;
};

/**
 * Creates a React Query options object for the search-optimized API.
 * Used by landing page containers to fetch search results for carousels.
 */
const makeSearchQuery = (
  params: ApiSearchOptimizedRetrieveParams,
  options: MakeSearchQueryOptions = {}
): UseQueryOptions<OptimizedSearchResponse> => {
  const {
    locationParams = {},
    enabled = true,
    searchSourceType = ROVER_SEARCH_SOURCE_TYPE_LANDING_PAGE,
  } = options;
  const fullParams = { ...params, ...locationParams };

  return {
    queryKey: getApiSearchOptimizedRetrieveQueryKey(fullParams),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      HTTP<OptimizedSearchResponse>({
        url: '/api/v7/search-optimized/',
        method: 'GET',
        params: fullParams,
        signal,
        headers: {
          [ROVER_SEARCH_SOURCE_HEADER]: searchSourceType.toString(),
        },
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled,
  };
};

export default makeSearchQuery;
